import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, HeartHandshake } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { useCourses } from "@/components/home/ProgrammesPreview";

const ProgrammeCount = () => {
  const n = useCourses().length;
  return <p className="font-display text-6xl font-medium">{n ? String(n).padStart(2, "0") : "—"}</p>;
};

const Tile = ({ className = "", delay = 0, children, testid }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={`group relative min-h-[300px] overflow-hidden rounded-[28px] ${className}`}
  >
    {children}
  </motion.div>
);

const PhotoBg = ({ src, alt }) => (
  <>
    <img src={src} alt={alt} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/30 to-transparent" />
  </>
);

export const Bento = () => (
  <section className="bg-sand px-5 pb-24 sm:px-10 lg:pb-36">
    <div className="mx-auto max-w-[1440px]">
      <Reveal className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <p className="eyebrow text-ochre">What we do</p>
          <h2 className="mt-4 max-w-2xl font-display text-4xl font-medium leading-[1] tracking-tight sm:text-6xl">Programmes rooted in Country, built for the future.</h2>
        </div>
        <Link to="/programmes" data-testid="bento-all-programmes-link" className="btn-line border-ink/30 text-ink hover:bg-ink hover:text-sand">All programmes <ArrowUpRight className="h-4 w-4" /></Link>
      </Reveal>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-12 md:auto-rows-[300px]">
        <Tile testid="bento-training" className="text-sand md:col-span-8 md:row-span-2 min-h-[420px]">
          <PhotoBg src="/img/community_workshop.jpg" alt="Community members at a hands-on workshop" />
          <div className="absolute inset-x-0 bottom-0 p-8 sm:p-12">
            <p className="eyebrow text-amber">01 — Training pathways</p>
            <h3 className="mt-4 max-w-lg font-display text-4xl font-medium leading-none sm:text-6xl">Learn skills that open doors.</h3>
            <p className="mt-4 max-w-md text-sm text-sand/85 sm:text-base">Work-ready, leadership and cultural programmes delivered with community, for community.</p>
          </div>
        </Tile>
        <Tile testid="bento-mentorship" delay={0.1} className="bg-gum p-8 text-sand md:col-span-4">
          <p className="eyebrow text-amber">02 — Mentoring</p>
          <p className="mt-6 font-display text-3xl italic leading-tight">“[Add a quote from an Elder, mentor or past participant here.]”</p>
          <p className="absolute bottom-8 left-8 font-mono text-xs uppercase tracking-[0.2em] text-sand/80">— [Name, role]</p>
        </Tile>
        <Tile testid="bento-stats" delay={0.2} className="flex flex-col justify-between bg-ink p-8 text-sand md:col-span-4">
          <p className="eyebrow text-amber">03 — Impact</p>
          <div className="grid grid-cols-2 gap-6">
            <div><ProgrammeCount /><p className="mt-1 text-xs text-sand/80">Programmes open now</p></div>
            <div><p className="font-display text-6xl font-medium text-ochre">[00]</p><p className="mt-1 text-xs text-sand/80">Community members supported</p></div>
          </div>
        </Tile>
        <Tile testid="bento-country" delay={0.1} className="text-sand md:col-span-6">
          <PhotoBg src="/img/eucalyptus_bush.jpg" alt="Tall eucalyptus trees in bushland" />
          <div className="absolute bottom-0 p-8">
            <p className="eyebrow text-amber">04 — Caring for Country</p>
            <h3 className="mt-3 font-display text-4xl font-medium leading-none">Land &amp; sea care</h3>
          </div>
        </Tile>
        <Tile testid="bento-donate" delay={0.2} className="bg-ochre p-8 text-sand md:col-span-6">
          <HeartHandshake className="h-10 w-10" strokeWidth={1.4} />
          <h3 className="mt-6 max-w-sm font-display text-4xl font-medium leading-none">Your gift keeps a learner on their path.</h3>
          <Link to="/donate" data-testid="bento-donate-button" className="absolute bottom-8 right-8 flex h-16 w-16 items-center justify-center rounded-full bg-sand text-ink transition-transform duration-500 group-hover:rotate-45">
            <ArrowUpRight className="h-6 w-6" />
          </Link>
        </Tile>
      </div>
    </div>
  </section>
);
